import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin } from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { loadGoogleMaps, isGoogleMapsLoaded, getGoogleMapsApi } from '../lib/googleMaps';
import { MapErrorBoundary } from './MapErrorBoundary';

interface TrailPoint {
  latitude: number;
  longitude: number;
  accuracy_m: number | null;
  created_at: string;
  category: string;
}

interface Props {
  reportId: string;
}

/**
 * Read-only Google map of the GPS path a technician walked while inspecting a
 * property. Fetches the per-report trail (cleaned + geofence-clipped
 * server-side) and draws the polyline plus decimated dot markers, mirroring the
 * live Inspections Dashboard rendering.
 *
 * Shown on the internal report surfaces — the manager review screen and the
 * report viewer — for Manager/Admin only; the trail endpoint is role-gated
 * server-side to match. It is NEVER part of what a client receives: in the
 * viewer it is rendered outside `#report-content`, which the print stylesheet
 * excludes, and the emailed PDF is generated separately by the server.
 */
export function InspectionTrailMap({ reportId }: Props) {
  return (
    <MapErrorBoundary>
      <InspectionTrailMapInner reportId={reportId} />
    </MapErrorBoundary>
  );
}

function InspectionTrailMapInner({ reportId }: Props) {
  const { t } = useTranslation('inspection');
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const trailRef = useRef<google.maps.Polyline | null>(null);
  const trailDotsRef = useRef<google.maps.Marker[]>([]);

  const [trail, setTrail] = useState<TrailPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [mapReady, setMapReady] = useState(isGoogleMapsLoaded());
  const [error, setError] = useState<string | null>(null);

  // Fetch the report's trail once.
  useEffect(() => {
    let active = true;
    setLoading(true);
    apiClient
      .get<{ points: TrailPoint[] }>(`/api/dashboard/trail/by-report/${reportId}`)
      .then((res) => {
        if (active) setTrail(res.points ?? []);
      })
      .catch((err) => {
        console.error('Failed to load inspection trail:', err);
        if (active) setTrail([]);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [reportId]);

  // Load the Maps API (only needed when there's actually a path to draw).
  useEffect(() => {
    if (loading || trail.length < 1 || mapReady) return;
    let active = true;
    loadGoogleMaps()
      .then(() => {
        if (!active) return;
        // Resolving is not proof the API is usable — check the global itself.
        if (isGoogleMapsLoaded()) setMapReady(true);
        else setError(t('trailMap.loadError'));
      })
      .catch((err) => {
        console.error('Failed to load Google Maps for the inspection trail:', err);
        if (active) setError(t('trailMap.loadError'));
      });
    return () => {
      active = false;
    };
  }, [loading, trail.length, mapReady, t]);

  // Create the map once the API is ready and we have points.
  useEffect(() => {
    if (!mapReady || !mapRef.current || mapInstanceRef.current || trail.length < 1) return;
    // Never touch the bare `google` global: it is a ReferenceError, not a
    // falsy value, when the third-party script isn't there.
    const maps = getGoogleMapsApi();
    if (!maps) {
      setError(t('trailMap.loadError'));
      return;
    }
    try {
      mapInstanceRef.current = new maps.Map(mapRef.current, {
        center: { lat: trail[0].latitude, lng: trail[0].longitude },
        zoom: 18,
        mapTypeId: maps.MapTypeId.HYBRID,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
        styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }],
      });
    } catch (err) {
      console.error('Failed to create the inspection trail map:', err);
      setError(t('trailMap.loadError'));
    }
  }, [mapReady, trail, t]);

  // Draw the polyline + decimated dots; fit the map to the path.
  useEffect(() => {
    const map = mapInstanceRef.current;
    const maps = getGoogleMapsApi();
    if (!mapReady || !map || !maps) return;

    if (trailRef.current) {
      trailRef.current.setMap(null);
      trailRef.current = null;
    }
    trailDotsRef.current.forEach((d) => d.setMap(null));
    trailDotsRef.current = [];

    if (trail.length < 1) return;

    const path = trail.map((p) => ({ lat: p.latitude, lng: p.longitude }));

    if (path.length >= 2) {
      trailRef.current = new maps.Polyline({
        path,
        map,
        strokeColor: '#2563eb',
        strokeOpacity: 0.9,
        strokeWeight: 3,
        zIndex: 400,
      });
    }

    // Decimate dot markers so a dense trail doesn't drop hundreds of pins; always
    // keep the start + latest.
    const MAX_DOTS = 40;
    const lastIdx = trail.length - 1;
    const step = Math.max(1, Math.ceil(trail.length / MAX_DOTS));
    const bounds = new maps.LatLngBounds();
    trail.forEach((p, idx) => {
      bounds.extend({ lat: p.latitude, lng: p.longitude });
      const isStart = idx === 0;
      const isEnd = idx === lastIdx;
      if (!isStart && !isEnd && idx % step !== 0) return;
      const dot = new maps.Marker({
        position: { lat: p.latitude, lng: p.longitude },
        map,
        zIndex: 450,
        title: isStart ? t('trailMap.start') : isEnd ? t('trailMap.latest') : undefined,
        icon: {
          path: maps.SymbolPath.CIRCLE,
          scale: isStart || isEnd ? 6 : 3,
          fillColor: isStart ? '#111827' : isEnd ? '#2563eb' : '#60a5fa',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: isStart || isEnd ? 2 : 1,
        },
      });
      trailDotsRef.current.push(dot);
    });

    if (trail.length === 1) {
      map.setCenter({ lat: trail[0].latitude, lng: trail[0].longitude });
      map.setZoom(18);
    } else {
      map.fitBounds(bounds, { top: 32, bottom: 32, left: 32, right: 32 });
    }
  }, [mapReady, trail, t]);

  if (loading) {
    return (
      <div className="rounded-lg border border-line bg-subtle p-4 text-sm text-theme-secondary">
        {t('trailMap.loading')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-4 text-sm text-red-600 dark:text-red-400">
        {error}
      </div>
    );
  }

  if (trail.length < 1) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-line bg-subtle p-4 text-sm text-theme-secondary">
        <MapPin size={16} className="flex-shrink-0" />
        <span>{t('trailMap.noPath')}</span>
      </div>
    );
  }

  return (
    <div
      ref={mapRef}
      className="w-full h-80 rounded-lg border border-line overflow-hidden"
    />
  );
}
